import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { ChevronRight } from 'lucide-react';
import { Button } from '@hqo/ui-atoms';
import { ActivityAction } from 'app/data/types/activity';

interface CardStatusFooterProps {
  updatedMeta: string;
  actionNeeded: boolean;
  /** Whether the card's primary action can be routed. Gates BOTH the action button and the
   *  "Details" affordance — a non-tappable card shows neither (just the meta). */
  tappable: boolean;
  action?: ActivityAction;
  actionLabel: string;
  onAction: () => void;
}

/** Footer for an OPEN request: relative "updated" meta + (when routable) an action button or a
 *  "Details" affordance. An unroutable card renders neither, so nothing dead-taps. */
export const CardStatusFooter = ({
  updatedMeta,
  actionNeeded,
  tappable,
  action,
  actionLabel,
  onAction,
}: CardStatusFooterProps): JSX.Element => {
  const intl = useIntl();
  const showButton = actionNeeded && tappable && Boolean(action);
  const showDetails = !showButton && tappable;
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onAction();
    },
    [onAction],
  );

  return (
    <div className="mt-3 flex items-center justify-between gap-2.5 border-t border-system-grey-light pt-3">
      <span className="truncate text-xs font-medium text-system-grey-dark-alt">{updatedMeta}</span>
      {showButton && (
        <Button
          type="button"
          onClick={handleClick}
          data-testid="activity-card-action"
          className="h-auto shrink-0 rounded-full px-4 py-2 text-[13px] font-semibold shadow-none"
        >
          {actionLabel}
        </Button>
      )}
      {showDetails && (
        <span className="flex shrink-0 items-center gap-0.5 text-[12.5px] font-semibold text-system-grey-dark-alt">
          {intl.formatMessage({ id: 'activity.action.details' })}
          <ChevronRight size={14} strokeWidth={2.25} />
        </span>
      )}
    </div>
  );
};
